import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PencilIcon, PlusIcon } from "@/components/developer/DashboardIcons";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useState } from "react";
import EducationForm from "./education/EducationForm";
import EducationList from "./education/EducationList";

const Education = () => {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editingEducation, setEditingEducation] = useState<any>(null);

  const { data: educations, isFetching, refetch } = useQuery({
    queryKey: ['education'],
    queryFn: async () => {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      const { data, error } = await supabase
        .from('education')
        .select('*')
        .eq('user_id', user.user.id)
        .order('start_date', { ascending: false });

      if (error) {
        toast({
          title: "Error",
          description: "Failed to fetch education data",
          variant: "destructive",
        });
        throw error;
      }

      return data;
    }
  });

  const handleSave = async (educationData: any) => {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('No user found');

      if (editingEducation) {
        // Update existing education entry
        const { error } = await supabase
          .from('education')
          .update({
            institution: educationData.institution,
            degree: educationData.degree,
            field_of_study: educationData.field_of_study,
            start_date: educationData.start_date,
            end_date: educationData.end_date || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingEducation.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Education entry updated successfully",
        });
      } else {
        // Create new education entry
        const { error } = await supabase
          .from('education')
          .insert({
            user_id: user.user.id,
            institution: educationData.institution,
            degree: educationData.degree,
            field_of_study: educationData.field_of_study,
            start_date: educationData.start_date,
            end_date: educationData.end_date || null,
          });

        if (error) throw error;

        toast({
          title: "Success",
          description: "Education entry created successfully",
        });
      }

      await refetch();
      setIsEditing(false);
      setEditingEducation(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save education entry",
        variant: "destructive",
      });
      console.error('Error saving education:', error);
    }
  };

  const handleEdit = (education: any) => {
    setEditingEducation(education);
    setIsEditing(true);
  };

  if (isFetching) {
    return (
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold">Education</h2>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-16 w-full bg-gray-200 rounded"></div>
          <div className="h-16 w-full bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  if (isEditing) {
    return (
      <Card className="p-6">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-semibold">Education</h2>
        </div>
        <EducationForm
          initialData={editingEducation}
          onSave={handleSave}
          onCancel={() => {
            setIsEditing(false);
            setEditingEducation(null);
          }}
        />
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between mb-4">
        <h2 className="text-xl font-semibold">Education</h2>
        <div className="flex gap-2">
          {educations && educations.length > 0 && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setEditingEducation(null);
                setIsEditing(true);
              }}
              className="gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              Add
            </Button>
          )}
        </div>
      </div>
      {(!educations || educations.length === 0) ? (
        <div className="text-center py-4">
          <p className="text-sm text-gray-500 mb-4">No education entries yet</p>
          <Button
            onClick={() => {
              setEditingEducation(null);
              setIsEditing(true);
            }}
            className="gap-2"
          >
            <PlusIcon className="h-4 w-4" />
            Add Education
          </Button>
        </div>
      ) : (
        <EducationList 
          educations={educations} 
          onEdit={handleEdit}
        />
      )}
    </Card>
  );
};

export default Education;